import { Button, Icon, Loader } from '@mairistem/composants';
import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { getImageDimensions } from '@/utils';

interface ImageUploaderProps {
  value?: string | null;
  description?: string | null;
  onChange?: (image: string | null) => void;
  onDelete?: () => void;
  maxSize?: number;
  allowedTypes?: string[];
  maxDimensions?: { width: number; height: number };
  size?: number;
  loading?: boolean;
}

const ImageUploader = (props: ImageUploaderProps) => {
  const {
    value,
    description,
    onChange,
    onDelete,
    maxSize = 3 * 1024 * 1024, // 3Mo par défaut
    allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/bmp'],
    maxDimensions,
    size = 120,
    loading = false,
  } = props;

  const [isDragging, setIsDragging] = React.useState(false);
  const [imageKey, setImageKey] = React.useState(0);
  const [hasImageError, setHasImageError] = React.useState(false);

  const baseKey = React.useRef(Math.random().toString(36).substring(2, 8));
  const valueRef = React.useRef(value);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const addTimestampParam = React.useCallback(
    (url: string): string => {
      // Ne pas ajouter le paramètre 't' pour les URLs S3 signées
      // car cela invaliderait la signature X-Amz-Signature
      if (url.includes('X-Amz-Signature')) {
        return url;
      }

      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}t=${baseKey.current}-${imageKey}`;
    },
    [imageKey],
  );

  const handleImageLoad = React.useCallback(() => {
    if (!_.isNil(valueRef.current)) {
      setHasImageError(false);
    }
  }, []);

  const handleImageError = React.useCallback(() => {
    setHasImageError(true);
  }, []);

  const validateImage = React.useCallback(
    async (file: File): Promise<boolean> => {
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format d'image non supporté. Utilisez PNG, JPG, JPEG ou BMP.");
        return false;
      }

      if (file.size > maxSize) {
        const maxSizeMo = Math.round(maxSize / (1024 * 1024));
        toast.error(`L'image ne doit pas dépasser ${maxSizeMo}Mo.`);
        return false;
      }

      if (maxDimensions) {
        try {
          const { width, height } = await getImageDimensions(file);
          if (width > maxDimensions.width || height > maxDimensions.height) {
            toast.error(
              `Les dimensions de l'image ne doivent pas dépasser ${maxDimensions.width} x ${maxDimensions.height} pixels.`,
            );
            return false;
          }
        } catch {
          toast.error("Impossible de lire l'image. Le fichier est peut-être corrompu.");
          return false;
        }
      }

      return true;
    },
    [allowedTypes, maxSize, maxDimensions],
  );

  const handleImageUpload = React.useCallback(
    async (file: File) => {
      if (!(await validateImage(file))) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        if (onChange) {
          onChange(imageData);
        }
      };
      reader.readAsDataURL(file);
    },
    [validateImage, onChange],
  );

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const { files } = e.dataTransfer;
      if (files.length > 0) {
        handleImageUpload(files[0]);
      }
    },
    [handleImageUpload],
  );

  const handleFileInputChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        await handleImageUpload(files[0]);
      }
      e.target.value = '';
    },
    [handleImageUpload],
  );

  const handleClickUpload = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, []);

  const handleRemoveImage = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onDelete) {
        onDelete();
      }
    },
    [onDelete],
  );
  React.useEffect(() => {
    if (value) {
      setHasImageError(false);
      setImageKey((prev) => prev + 1);
    }

    valueRef.current = value;
  }, [value]);

  const acceptFormats = allowedTypes.map((type) => `.${type.split('/')[1]}`).join(',');
  const maxSizeMo = Math.round(maxSize / (1024 * 1024));
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptFormats}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <div
        className={classNames('image-uploader', {
          dragging: isDragging,
        })}
        role="button"
        tabIndex={0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClickUpload}
        onKeyDown={handleKeyDown}
      >
        <div style={{ textAlign: 'center', width: '100%' }}>
          {!loading && value && !hasImageError && (
            <Button icon="delete" circular size="tiny" basic negative onClick={handleRemoveImage} />
          )}
          <div
            className="image-container"
            style={{
              height: size,
            }}
          >
            {loading && <Loader active />}
            {value && imageKey > 0 && !hasImageError && (
              <img
                key={imageKey}
                // Le "t" permet de forcer le rechargement de l'image
                // car l'URL ne changeant pas,
                // il récupère le cache et ne refait pas la requête sinon
                src={
                  _.startsWith(value, 'data:')
                    ? value // base64 image qu'on vient de rajouter
                    : addTimestampParam(value)
                }
                alt="Logo"
                style={{
                  maxHeight: size,
                  display: loading ? 'none' : 'block',
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
            {(!value || hasImageError) && !loading && (
              <Icon name="ImagePlus" iconSet="Lucide" size={size} strokeWidth={0.3} opacity={0.5} />
            )}
          </div>
          <div className="image-description">
            <span>
              Cliquez ou glissez-déposez une image
              <br />
              <span>({allowedTypes.map((t) => t.split('/')[1].toUpperCase()).join(', ')})</span>
            </span>
            <span>
              Taille max :
              <br />
              {maxSizeMo} Mo
              {maxDimensions && (
                <>
                  <br />
                  {maxDimensions.width}
                  {' x '}
                  {maxDimensions.height}
                  {' pixels'}
                </>
              )}
            </span>
            {description && <span>{description}</span>}
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageUploader;
